// License Create Page — Issue new licenses
import React, { useState, useEffect } from 'react';
import { useToast } from '@/components/shared/ToastProvider';
import { useNavigate, Link, useSearchParams } from 'react-router';
import { apiRequest } from '@/lib/supabase';
import { PRODUCT_NAME } from '@/lib/brand';
import { ArrowLeft, Plus, X, Copy, Download, ShieldAlert, Check, Server, Building2, Cloud } from 'lucide-react';
import { provisionSaasOrg, tierLabel, SAAS_LICENSE_TIERS, type SaasLicenseTier } from '@/lib/api/trust';
import { fetchModulesCatalog, derivedModulesForTier, type ModuleCatalogRow, type TierGrantRow } from '@/lib/api/modules';

interface Organization {
  id: string;
  name: string;
}

// Fields map 1:1 onto backend createLicenseSchema (backend/src/routes/license.routes.ts) —
// camelCase, exact names. Everything the schema does NOT name (product name, grace period,
// API call cap, hardware id, free-form features) is operator-only UI and gets folded into
// the `metadata` object on submit rather than sent as top-level fields, since the backend
// would otherwise silently strip mismatched keys.
interface CreateLicenseForm {
  organizationId: string;
  tier: string;
  vendorLimit: number;
  expiresAt: string;
  scope: 'org' | 'platform';
  // Platform-scope-only pool fields (submitted only when scope === 'platform').
  includedOrgSeats: number;
  billingMode: 'bundled' | 'metered' | 'hybrid';
  overagePolicy: 'soft' | 'block';
  parentLicenseId: string;
  // Operator-only fields — folded into `metadata` on submit.
  productName: string;
  gracePeriodDays: number;
  maxApiCallsPerDay: number;
  hardwareId: string;
  features: Record<string, string | boolean | number>;
  // Free-form extra metadata (JSON text), merged into the final metadata object.
  metadata: string;
}

// Shape of the POST /licenses response. jwt_token is delivered ONLY here (issue-time,
// one-shot) — general GET reads never include it again after this response.
interface CreatedLicense {
  id: string;
  license_id?: string;
  jwt_token?: string;
}

const TIER_PRESETS: Record<string, Partial<CreateLicenseForm>> = {
  trial: { vendorLimit: 5, maxApiCallsPerDay: 100, gracePeriodDays: 0 },
  startup: { vendorLimit: 10, maxApiCallsPerDay: 1000, gracePeriodDays: 7 },
  professional: { vendorLimit: 25, maxApiCallsPerDay: 5000, gracePeriodDays: 14 },
  enterprise: { vendorLimit: 100, maxApiCallsPerDay: 50000, gracePeriodDays: 30 },
};

const FEATURE_TEMPLATES: Record<string, Record<string, boolean | number>> = {
  trial: { sso: false, api_access: true, advanced_reporting: false, custom_branding: false, max_vendors: 10 },
  startup: { sso: false, api_access: true, advanced_reporting: false, custom_branding: false, max_vendors: 50 },
  professional: { sso: true, api_access: true, advanced_reporting: true, custom_branding: false, max_vendors: 200 },
  enterprise: { sso: true, api_access: true, advanced_reporting: true, custom_branding: true, max_vendors: -1 },
};

export function LicenseCreatePage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // BP8 convert-to-paid pre-fill: a trial's org + pool flow in as ?org=&seats=.
  const prefillOrg = searchParams.get('org') || '';
  const prefillSeats = searchParams.get('seats');
  const convertedFrom = searchParams.get('convertedFrom');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newFeatureKey, setNewFeatureKey] = useState('');
  const [newFeatureVal, setNewFeatureVal] = useState('');
  // Catalog-driven licensed modules. `moduleSel` holds ONLY the operator's
  // explicit choices (keyed by modules.code) — tier-derived modules are shown
  // checked+disabled and are NOT set here; the backend re-derives them at sign
  // time via resolveModules (explicit overlays derived), so we never double-set.
  const [moduleSel, setModuleSel] = useState<Record<string, boolean>>({});
  const [moduleCatalog, setModuleCatalog] = useState<ModuleCatalogRow[]>([]);
  const [tierGrants, setTierGrants] = useState<TierGrantRow[]>([]);
  // One-time reveal panel state. Set only from the issue-time POST response —
  // never re-fetched, never logged. Null until a license has been created.
  const [created, setCreated] = useState<CreatedLicense | null>(null);
  const [copied, setCopied] = useState(false);

  // 18H §4.1 — class picker up front. install/platform keep the classic form
  // below (POST /licenses); saas is a completely different commercial record
  // with no JWT — it provisions via POST /trust/provision instead (17_ §5:
  // "no JWT, no phone-home, no /validate involvement ever").
  const [licenseClass, setLicenseClass] = useState<'install' | 'platform' | 'saas'>('install');
  const [whiteLabel, setWhiteLabel] = useState(false);

  // ── saas path state ──────────────────────────────────────────────────────
  // organizationId = the license-server's own account record ("who pays" —
  // same organizations table as install/platform). Separately, the hosted
  // TPRM7 org this license binds to is EITHER an existing org (tprm7OrgId) OR
  // a brand-new one the bridge creates (newOrg) — 17_ §5 "explicit, never
  // inferred"; these two org spaces are cross-database, not the same row.
  const [saasOrgId, setSaasOrgId] = useState('');
  const [saasTprm7Mode, setSaasTprm7Mode] = useState<'existing' | 'new'>('new');
  const [saasTprm7OrgId, setSaasTprm7OrgId] = useState('');
  const [saasNewOrgName, setSaasNewOrgName] = useState('');
  const [saasAdminEmail, setSaasAdminEmail] = useState('');
  const [saasAdminName, setSaasAdminName] = useState('');
  const [saasTier, setSaasTier] = useState<SaasLicenseTier>('free');
  const [saasBillingEmail, setSaasBillingEmail] = useState('');
  const [saasLoading, setSaasLoading] = useState(false);
  const [saasErrors, setSaasErrors] = useState<Record<string, string>>({});
  const [saasCreated, setSaasCreated] = useState<{ licenseId: string; tier: string } | null>(null);

  const defaultExpiry = new Date();
  defaultExpiry.setFullYear(defaultExpiry.getFullYear() + 1);

  const [form, setForm] = useState<CreateLicenseForm>({
    organizationId: prefillOrg,
    productName: `${PRODUCT_NAME} Pro`,
    tier: 'professional',
    vendorLimit: prefillSeats && Number(prefillSeats) > 0 ? Number(prefillSeats) : 25,
    expiresAt: defaultExpiry.toISOString().split('T')[0],
    gracePeriodDays: 14,
    maxApiCallsPerDay: 5000,
    hardwareId: '',
    features: { ...FEATURE_TEMPLATES.professional },
    metadata: convertedFrom ? JSON.stringify({ converted_from_trial: convertedFrom }, null, 2) : '{}',
    scope: 'org',
    includedOrgSeats: 0,
    billingMode: 'bundled',
    overagePolicy: 'soft',
    parentLicenseId: '',
  });

  useEffect(() => {
    // Backend returns { data: org[], pagination }. Use the real list; never fall back
    // to fabricated orgs (a license must be created against a real organization).
    apiRequest<{ data: Organization[] }>('/organizations?limit=100')
      .then(resp => setOrganizations(Array.isArray((resp as any)?.data) ? (resp as any).data : (Array.isArray(resp as any) ? (resp as any) : [])))
      .catch(() => setOrganizations([]));
  }, []);

  useEffect(() => {
    // Catalog of purchasable modules + the ACTIVE license_tier grants. Grants
    // ship dark, so tierGrants is empty until an operator flips a row live —
    // meaning derived modules only appear checked+disabled once the backend
    // will actually derive them.
    fetchModulesCatalog()
      .then(({ modules, tierGrants }) => { setModuleCatalog(modules); setTierGrants(tierGrants); })
      .catch(() => { setModuleCatalog([]); setTierGrants([]); });
  }, []);

  const applyTierPreset = (tier: string) => {
    const preset = TIER_PRESETS[tier] || {};
    const features = FEATURE_TEMPLATES[tier] || {};
    setForm(prev => ({ ...prev, tier, ...preset, features: { ...features } }));
  };

  const set = (key: keyof CreateLicenseForm, value: unknown) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => { const e = { ...prev }; delete e[key]; return e; });
  };

  const toggleFeature = (key: string, value: string | number | boolean) => {
    setForm(prev => ({
      ...prev,
      features: { ...prev.features, [key]: typeof value === 'boolean' ? !value : value },
    }));
  };

  const addFeature = () => {
    if (!newFeatureKey.trim()) return;
    let val: boolean | number | string = newFeatureVal;
    if (newFeatureVal === 'true') val = true;
    else if (newFeatureVal === 'false') val = false;
    else if (!isNaN(Number(newFeatureVal)) && newFeatureVal !== '') val = Number(newFeatureVal);
    setForm(prev => ({ ...prev, features: { ...prev.features, [newFeatureKey.trim()]: val } }));
    setNewFeatureKey('');
    setNewFeatureVal('');
  };

  const removeFeature = (key: string) => {
    setForm(prev => {
      const f = { ...prev.features };
      delete f[key];
      return { ...prev, features: f };
    });
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.organizationId) errs.organizationId = 'Organization is required';
    if (!form.productName.trim()) errs.productName = 'Product name is required';
    if (form.vendorLimit < 1) errs.vendorLimit = 'At least 1 seat required';
    if (!form.expiresAt) errs.expiresAt = 'Expiry date is required';
    else if (new Date(form.expiresAt) < new Date()) errs.expiresAt = 'Expiry must be in the future';
    try { JSON.parse(form.metadata); } catch { errs.metadata = 'Invalid JSON'; }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      // Send ONLY the operator's explicit truthy selections. Tier-derived
      // modules are excluded on purpose — the backend re-derives them from the
      // active grants at sign time (resolveModules), so sending them here would
      // be redundant and would flatten their grant term to perpetual.
      const derived = derivedModulesForTier(form.tier, tierGrants);
      const activeModules = Object.entries(moduleSel).reduce<Record<string, boolean>>(
        (acc, [k, v]) => { if (v && !derived[k]) acc[k] = true; return acc; },
        {}
      );
      // Everything the backend schema doesn't name is operator-only — fold it into
      // metadata (free-form passthrough) instead of sending it as a top-level field.
      let extraMetadata: Record<string, unknown> = {};
      try { extraMetadata = JSON.parse(form.metadata); } catch { extraMetadata = {}; }
      const metadata: Record<string, unknown> = {
        productName: form.productName,
        gracePeriodDays: form.gracePeriodDays,
        maxApiCallsPerDay: form.maxApiCallsPerDay,
        ...(form.hardwareId ? { hardwareId: form.hardwareId } : {}),
        ...(Object.keys(form.features).length > 0 ? { features: form.features } : {}),
        ...extraMetadata,
      };
      // Body matches backend createLicenseSchema exactly: camelCase keys, `scope` selects
      // org vs platform, and the platform pool fields are only sent when scope=platform.
      const payload: Record<string, unknown> = {
        organizationId: form.organizationId,
        tier: form.tier,
        vendorLimit: form.vendorLimit,
        expiresAt: new Date(form.expiresAt).toISOString(),
        metadata,
        scope: form.scope,
      };
      if (Object.keys(activeModules).length > 0) payload.modules = activeModules;
      if (form.scope === 'platform') {
        payload.includedOrgSeats = form.includedOrgSeats;
        payload.billingMode = form.billingMode;
        payload.overagePolicy = form.overagePolicy;
        if (form.parentLicenseId.trim()) payload.parentLicenseId = form.parentLicenseId.trim();
        // 18G §2.2 B6 / D-41 — white-label channel flag, platform-scope only.
        payload.whiteLabel = whiteLabel;
      }
      const result = await apiRequest<CreatedLicense>('/licenses', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      // Do NOT navigate away here — this response is the only place jwt_token
      // is ever delivered outside the deliberate reveal endpoint. Show the
      // one-time reveal panel first; the operator dismisses it explicitly.
      setCreated(result);
    } catch (err) {
      // Surface the real failure — do NOT pretend success and navigate away.
      toast({ kind: 'error', title: 'Failed to create license', body: err instanceof Error ? err.message : 'Request failed' });
    } finally {
      setLoading(false);
    }
  };

  const validateSaas = (): boolean => {
    const errs: Record<string, string> = {};
    if (!saasOrgId) errs.saasOrgId = 'The paying account organization is required';
    if (saasTprm7Mode === 'existing' && !saasTprm7OrgId.trim()) errs.saasTprm7OrgId = 'TPRM7 org id is required';
    if (saasTprm7Mode === 'new') {
      if (!saasNewOrgName.trim()) errs.saasNewOrgName = 'Hosted org name is required';
      if (!saasAdminEmail.trim()) errs.saasAdminEmail = 'Admin email is required';
    }
    setSaasErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // 17_ §5 — a saas org is provisioned (not "created" the way an install
  // license is): explicit account binding (organizationId, this server's own
  // record — "who pays") + an explicit TPRM7-side org binding (existing id OR
  // a brand-new hosted org) + tier + contacts. Zero-price 'free' is a
  // first-class tier here, not a workaround.
  const handleSaasSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateSaas()) return;
    setSaasLoading(true);
    try {
      const contacts: Array<{ role: 'admin' | 'billing'; email: string; name?: string | null; source: string }> = [];
      if (saasAdminEmail.trim()) {
        contacts.push({ role: 'admin', email: saasAdminEmail.trim(), name: saasAdminName.trim() || null, source: 'console' });
      }
      if (saasBillingEmail.trim()) {
        contacts.push({ role: 'billing', email: saasBillingEmail.trim(), source: 'console' });
      }
      const body: Record<string, unknown> = {
        organizationId: saasOrgId,
        tier: saasTier,
        trigger: 'manual',
        acquisitionSource: 'concierge',
        ...(contacts.length > 0 ? { contacts } : {}),
      };
      if (saasTprm7Mode === 'existing') {
        body.tprm7OrgId = saasTprm7OrgId.trim();
      } else {
        body.newOrg = { name: saasNewOrgName.trim(), admin_email: saasAdminEmail.trim(), admin_name: saasAdminName.trim() || undefined };
      }
      const result = await provisionSaasOrg(body);
      setSaasCreated({ licenseId: result.license.license_id, tier: result.saasDetails.tier });
    } catch (err) {
      setSaasErrors({ _submit: err instanceof Error ? err.message : 'Failed to provision saas org' });
    } finally {
      setSaasLoading(false);
    }
  };

  const copyToken = () => {
    if (!created?.jwt_token) return;
    navigator.clipboard.writeText(created.jwt_token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadToken = () => {
    if (!created?.jwt_token) return;
    const filename = `${created.license_id || created.id}.license`;
    const blob = new Blob([created.jwt_token], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const fieldStyle = (hasError?: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '9px 12px',
    borderRadius: '8px',
    border: `1px solid ${hasError ? '#DC2626' : 'var(--border)'}`,
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    marginBottom: '6px',
  };

  // Once the license is created, replace the form with a one-time reveal panel.
  // jwt_token is only ever delivered on this response — if it's missing (e.g. an
  // older backend or a reveal-disabled deployment) degrade gracefully rather than
  // pretending we have it.
  if (created) {
    const displayKey = created.license_id || created.id;
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">License issued</h1>
            <p className="page-subtitle">License {displayKey} was created successfully.</p>
          </div>
        </div>

        <div className="card" style={{ border: '1px solid var(--ps-pill-amber-bg)', background: 'var(--ps-pill-amber-bg)' }}>
          <div className="card-header" style={{ background: 'transparent' }}>
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--ps-pill-amber-tx)' }}>
              <ShieldAlert size={16} />
              {created.jwt_token ? 'License issued — copy the license token now' : 'License issued'}
            </span>
          </div>
          <div className="card-body">
            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>License Key</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <code style={{ fontSize: '13px', background: 'var(--bg-muted)', padding: '6px 10px', borderRadius: '6px', flex: 1 }}>{displayKey}</code>
              </div>
            </div>

            {created.jwt_token ? (
              <>
                <div style={{ padding: '10px 12px', background: 'var(--ps-pill-amber-bg)', borderRadius: '6px', marginBottom: '14px', fontSize: '13px', color: 'var(--ps-pill-amber-tx)', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <ShieldAlert size={14} style={{ marginTop: '1px', flexShrink: 0 }} />
                  <span>
                    This token is the <strong>install credential</strong> for this license. It is shown <strong>once</strong> —
                    it will not be shown again on this or any other screen. Copy or download it now and store it securely.
                    If you lose it, use "Reveal license token" on the license detail page (audit-logged).
                  </span>
                </div>

                <label style={labelStyle}>License Token (JWT)</label>
                {/* ps-no-capture: rrweb blockClass. Issue-time is the one place
                    jwt_token is delivered without an audit-logged reveal — fence
                    the textarea so it never reaches a recording. */}
                <textarea
                  className="ps-no-capture"
                  readOnly
                  value={created.jwt_token}
                  onFocus={e => e.target.select()}
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    resize: 'vertical',
                    outline: 'none',
                    boxSizing: 'border-box',
                    background: 'var(--surface)',
                    overflowY: 'auto',
                    marginBottom: '12px',
                  }}
                />

                <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                  <button
                    type="button"
                    onClick={copyToken}
                    style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    onClick={downloadToken}
                    style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
                  >
                    <Download size={14} /> Download
                  </button>
                </div>
              </>
            ) : (
              <div style={{ padding: '10px 12px', background: 'var(--ps-pill-gray-bg)', borderRadius: '6px', marginBottom: '4px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                This response did not include the license token. Use <strong>Reveal license token</strong> on the license
                detail page to retrieve it (admin-only, audit-logged).
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => navigate(`/licenses/${created.id}`)}
            style={{ padding: '10px 16px', borderRadius: '8px', background: 'var(--accent)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // saas provisioning success — no token to reveal (D-34: no JWT ever exists
  // for a saas record).
  if (saasCreated) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">Trust org provisioned</h1>
            <p className="page-subtitle">License {saasCreated.licenseId} ({tierLabel(saasCreated.tier)}) is live. Entitlements + the right-party invite were sent by the bridge.</p>
          </div>
        </div>
        <div className="card" style={{ border: '1px solid var(--ps-pill-green-bg)', background: 'var(--ps-pill-green-bg)' }}>
          <div className="card-header" style={{ background: 'transparent' }}>
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--ps-pill-green-tx)' }}>
              <Check size={16} /> No credential to hand off
            </span>
          </div>
          <div className="card-body" style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            saas-class licenses carry no JWT and never phone home — entitlements are enforced entirely on the TPRM7 side.
            The right-party contact receives an invite email directly; there is nothing to copy or download here.
          </div>
        </div>
        <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
          <button type="button" onClick={() => navigate('/licenses')} style={{ padding: '10px 16px', borderRadius: '8px', background: 'var(--accent)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  // Sidebar summary rows — built here (rather than inline in JSX) so the platform-only
  // pool rows can be spliced in conditionally without a messy inline ternary spread.
  const summaryRows: [string, React.ReactNode][] = [
    ['Org', organizations.find(o => o.id === form.organizationId)?.name || '—'],
    ['Product', form.productName || '—'],
    ['Tier', <span style={{ textTransform: 'capitalize' }}>{form.tier}</span>],
    ['Scope', <span style={{ textTransform: 'capitalize' }}>{form.scope}</span>],
    ['Seats', form.vendorLimit],
    ['Expires', form.expiresAt ? new Date(form.expiresAt).toLocaleDateString() : '—'],
    ['Grace', `${form.gracePeriodDays}d`],
    ['API/day', form.maxApiCallsPerDay.toLocaleString()],
  ];
  if (form.scope === 'platform') {
    summaryRows.push(
      ['Org Seats Included', form.includedOrgSeats],
      ['Billing Mode', <span style={{ textTransform: 'capitalize' }}>{form.billingMode}</span>],
      ['Overage Policy', <span style={{ textTransform: 'capitalize' }}>{form.overagePolicy}</span>],
    );
    if (form.parentLicenseId.trim()) summaryRows.push(['Parent License', form.parentLicenseId]);
  }

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link to="/licenses" style={{ color: 'var(--text-secondary)', display: 'flex' }}>
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="page-title">{convertedFrom ? 'Convert Trial to Paid' : 'Issue New License'}</h1>
            <p className="page-subtitle">
              {convertedFrom
                ? `Issuing a paid license to convert trial ${convertedFrom}. Confirm the terms below.`
                : 'Create and assign a license to an organization'}
            </p>
          </div>
        </div>
      </div>

      {/* 18H §4.1 — class picker up front. install/platform (this server's own
          JWT-bearing ledger) vs saas (17_ §5 commercial-only, provisioned via
          the trust bridge, no JWT ever). */}
      {!convertedFrom && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">License class</span></div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {([
              { key: 'install' as const, label: 'Install', icon: Server, body: 'On-prem sovereignty — phone-home, dark trust.' },
              { key: 'platform' as const, label: 'Platform', icon: Building2, body: 'White-label reseller channel — dark trust.' },
              { key: 'saas' as const, label: 'SaaS', icon: Cloud, body: 'Our hosted plane — no JWT, entitlements via TPRM7.' },
            ]).map(({ key, label, icon: IconCmp, body }) => (
              <button
                key={key} type="button" onClick={() => setLicenseClass(key)}
                style={{
                  textAlign: 'left', padding: '12px 14px', borderRadius: 10,
                  border: `1.5px solid ${licenseClass === key ? 'var(--accent)' : 'var(--border)'}`,
                  background: licenseClass === key ? 'var(--accent-light)' : 'var(--surface)', cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <IconCmp size={15} color={licenseClass === key ? 'var(--accent)' : 'var(--text-secondary)'} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: licenseClass === key ? 'var(--accent)' : 'var(--text-primary)' }}>{label}</span>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{body}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {licenseClass === 'saas' ? (
        <form onSubmit={handleSaasSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="card">
                <div className="card-header"><span className="card-title">Account &amp; hosted org binding</span></div>
                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Paying account organization *</label>
                    <select value={saasOrgId} onChange={(e) => setSaasOrgId(e.target.value)} style={fieldStyle(!!saasErrors.saasOrgId)}>
                      <option value="">Select organization...</option>
                      {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                    {saasErrors.saasOrgId && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '4px' }}>{saasErrors.saasOrgId}</div>}
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
                      This server's own account record — "who pays." Independent of the hosted TPRM7 org below (D-34, explicit never inferred).
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Hosted TPRM7 org</label>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: 8 }}>
                      {(['new', 'existing'] as const).map((m) => (
                        <button
                          key={m} type="button" onClick={() => setSaasTprm7Mode(m)}
                          style={{ flex: 1, padding: '8px 10px', borderRadius: '8px', border: `1px solid ${saasTprm7Mode === m ? 'var(--accent)' : 'var(--border)'}`, background: saasTprm7Mode === m ? 'var(--accent-light)' : 'var(--surface)', color: saasTprm7Mode === m ? 'var(--accent)' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px', fontWeight: saasTprm7Mode === m ? 600 : 400 }}
                        >
                          {m === 'new' ? 'Create new hosted org' : 'Bind to existing hosted org'}
                        </button>
                      ))}
                    </div>
                    {saasTprm7Mode === 'existing' ? (
                      <>
                        <input type="text" value={saasTprm7OrgId} onChange={(e) => setSaasTprm7OrgId(e.target.value)} placeholder="TPRM7 organizations.id" style={fieldStyle(!!saasErrors.saasTprm7OrgId)} />
                        {saasErrors.saasTprm7OrgId && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '4px' }}>{saasErrors.saasTprm7OrgId}</div>}
                      </>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div style={{ gridColumn: '1/-1' }}>
                          <input type="text" value={saasNewOrgName} onChange={(e) => setSaasNewOrgName(e.target.value)} placeholder="Hosted org name *" style={fieldStyle(!!saasErrors.saasNewOrgName)} />
                          {saasErrors.saasNewOrgName && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '4px' }}>{saasErrors.saasNewOrgName}</div>}
                        </div>
                        <div>
                          <input type="email" value={saasAdminEmail} onChange={(e) => setSaasAdminEmail(e.target.value)} placeholder="Admin email *" style={fieldStyle(!!saasErrors.saasAdminEmail)} />
                          {saasErrors.saasAdminEmail && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '4px' }}>{saasErrors.saasAdminEmail}</div>}
                        </div>
                        <div>
                          <input type="text" value={saasAdminName} onChange={(e) => setSaasAdminName(e.target.value)} placeholder="Admin name" style={fieldStyle()} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header"><span className="card-title">Tier &amp; contacts</span></div>
                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Tier</label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {SAAS_LICENSE_TIERS.map((t) => (
                        <button
                          key={t} type="button" onClick={() => setSaasTier(t)}
                          style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${saasTier === t ? 'var(--accent)' : 'var(--border)'}`, background: saasTier === t ? 'var(--accent-light)' : 'var(--surface)', color: saasTier === t ? 'var(--accent)' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px', fontWeight: saasTier === t ? 600 : 400 }}
                        >
                          {tierLabel(t)}
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
                      Zero-price 'free' is a first-class vault, not a workaround — every hosted org gets one from birth.
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Billing contact email (optional)</label>
                    <input type="email" value={saasBillingEmail} onChange={(e) => setSaasBillingEmail(e.target.value)} placeholder="billing@company.com" style={fieldStyle()} />
                  </div>
                </div>
              </div>

              {saasErrors._submit && (
                <div style={{ padding: '10px 12px', background: 'var(--ps-pill-red-bg)', borderRadius: '6px', fontSize: '13px', color: 'var(--ps-pill-red-tx)' }}>{saasErrors._submit}</div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="card">
                <div className="card-header"><span className="card-title">Summary</span></div>
                <div className="card-body">
                  <table style={{ width: '100%' }}>
                    <tbody>
                      {([
                        ['Account org', organizations.find((o) => o.id === saasOrgId)?.name || '—'],
                        ['Hosted org', saasTprm7Mode === 'existing' ? (saasTprm7OrgId || '—') : (saasNewOrgName || '—')],
                        ['Tier', tierLabel(saasTier)],
                        ['Class', 'SaaS'],
                      ] as [string, React.ReactNode][]).map(([k, v]) => (
                        <tr key={k}>
                          <td style={{ padding: '6px 0', fontSize: '12px', color: 'var(--text-muted)', width: '45%' }}>{k}</td>
                          <td style={{ padding: '6px 0', fontSize: '12px', fontWeight: 500 }}>{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <button
                type="submit" disabled={saasLoading}
                style={{ padding: '12px', borderRadius: '8px', background: saasLoading ? 'var(--accent-muted)' : 'var(--accent)', color: 'white', border: 'none', cursor: saasLoading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 600 }}
              >
                {saasLoading ? 'Provisioning...' : 'Provision trust org'}
              </button>
              <Link to="/licenses" style={{ textAlign: 'center', fontSize: '13px', color: 'var(--text-secondary)', textDecoration: 'none' }}>Cancel</Link>
            </div>
          </div>
        </form>
      ) : (
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
          {/* Main form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Basic Info */}
            <div className="card">
              <div className="card-header"><span className="card-title">License Information</span></div>
              <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Organization *</label>
                  <select
                    value={form.organizationId}
                    onChange={e => set('organizationId', e.target.value)}
                    style={fieldStyle(!!errors.organizationId)}
                  >
                    <option value="">Select organization...</option>
                    {organizations.map(o => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                  {errors.organizationId && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '4px' }}>{errors.organizationId}</div>}
                  <div style={{ marginTop: '6px' }}>
                    <Link to="/organizations" style={{ fontSize: '11px', color: 'var(--accent)', textDecoration: 'none' }}>
                      + Create new organization
                    </Link>
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Product Name *</label>
                  <input type="text" value={form.productName} onChange={e => set('productName', e.target.value)} style={fieldStyle(!!errors.productName)} />
                  {errors.productName && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '4px' }}>{errors.productName}</div>}
                </div>

                <div>
                  <label style={labelStyle}>Tier</label>
                  <select value={form.tier} onChange={e => applyTierPreset(e.target.value)} style={fieldStyle()}>
                    <option value="trial">Trial</option>
                    <option value="startup">Startup</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Total Seats *</label>
                  <input type="number" min="1" value={form.vendorLimit} onChange={e => set('vendorLimit', parseInt(e.target.value))} style={fieldStyle(!!errors.vendorLimit)} />
                  {errors.vendorLimit && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '4px' }}>{errors.vendorLimit}</div>}
                </div>

                <div>
                  <label style={labelStyle}>Expiry Date *</label>
                  <input type="date" required value={form.expiresAt} onChange={e => set('expiresAt', e.target.value)} style={fieldStyle(!!errors.expiresAt)} />
                  {errors.expiresAt && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '4px' }}>{errors.expiresAt}</div>}
                </div>

                <div>
                  <label style={labelStyle}>Grace Period (days)</label>
                  <input type="number" min="0" value={form.gracePeriodDays} onChange={e => set('gracePeriodDays', parseInt(e.target.value))} style={fieldStyle()} />
                </div>

                <div>
                  <label style={labelStyle}>API Calls / Day</label>
                  <input type="number" min="0" value={form.maxApiCallsPerDay} onChange={e => set('maxApiCallsPerDay', parseInt(e.target.value))} style={fieldStyle()} />
                </div>

                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Hardware ID (optional — locks to device)</label>
                  <input type="text" value={form.hardwareId} onChange={e => set('hardwareId', e.target.value)} placeholder="e.g. HWID-XXXX-XXXX-XXXX" style={fieldStyle()} />
                </div>
              </div>
            </div>

            {/* Scope */}
            <div className="card">
              <div className="card-header"><span className="card-title">License Scope</span></div>
              <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Scope *</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {(['org', 'platform'] as const).map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => set('scope', s)}
                        style={{
                          flex: 1,
                          padding: '9px 12px',
                          borderRadius: '8px',
                          border: `1px solid ${form.scope === s ? 'var(--accent)' : 'var(--border)'}`,
                          background: form.scope === s ? 'var(--accent-light)' : 'var(--surface)',
                          color: form.scope === s ? 'var(--accent)' : 'var(--text-secondary)',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: form.scope === s ? 600 : 400,
                        }}
                      >
                        {s === 'platform' ? 'Platform (reseller pool)' : 'Org (single organization)'}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
                    Platform licenses grant a seat pool a reseller/parent org can allocate to sub-orgs.
                  </div>
                </div>

                {form.scope === 'platform' && (
                  <>
                    <div>
                      <label style={labelStyle}>Included Org Seats</label>
                      <input type="number" min="0" value={form.includedOrgSeats} onChange={e => set('includedOrgSeats', parseInt(e.target.value) || 0)} style={fieldStyle()} />
                    </div>

                    <div>
                      <label style={labelStyle}>Billing Mode</label>
                      <select value={form.billingMode} onChange={e => set('billingMode', e.target.value)} style={fieldStyle()}>
                        <option value="bundled">Bundled</option>
                        <option value="metered">Metered</option>
                        <option value="hybrid">Hybrid</option>
                      </select>
                    </div>

                    <div>
                      <label style={labelStyle}>Overage Policy</label>
                      <select value={form.overagePolicy} onChange={e => set('overagePolicy', e.target.value)} style={fieldStyle()}>
                        <option value="soft">Soft (warn)</option>
                        <option value="block">Block</option>
                      </select>
                    </div>

                    <div>
                      <label style={labelStyle}>Parent License ID (optional)</label>
                      <input type="text" value={form.parentLicenseId} onChange={e => set('parentLicenseId', e.target.value)} placeholder="Existing platform license ID" style={fieldStyle()} />
                    </div>

                    <div style={{ gridColumn: '1/-1' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input type="checkbox" checked={whiteLabel} onChange={(e) => setWhiteLabel(e.target.checked)} style={{ accentColor: 'var(--accent)', width: 14, height: 14 }} />
                        <span style={{ fontSize: '13px' }}>White-label channel (D-41)</span>
                      </label>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                        Formalizes this platform license as a branding-rights partner channel (18G §2.2 B6).
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Features */}
            <div className="card">
              <div className="card-header"><span className="card-title">Feature Flags</span></div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                  {Object.entries(form.features).map(([key, value]) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--bg-secondary)', borderRadius: '6px', gap: '8px' }}>
                      <span style={{ fontSize: '12px', flex: 1 }}>{key.replace(/_/g, ' ')}</span>
                      {typeof value === 'boolean' ? (
                        <button type="button" onClick={() => toggleFeature(key, value)} style={{ padding: '2px 8px', borderRadius: '4px', border: 'none', background: value ? 'var(--ps-pill-green-bg)' : 'var(--ps-pill-red-bg)', color: value ? 'var(--ps-pill-green-tx)' : 'var(--ps-pill-red-tx)', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>
                          {value ? 'ON' : 'OFF'}
                        </button>
                      ) : (
                        <input type="text" value={String(value)} onChange={e => {
                          const v = e.target.value;
                          const num = Number(v);
                          setForm(prev => ({ ...prev, features: { ...prev.features, [key]: isNaN(num) || v === '' ? v : num } }));
                        }} style={{ width: '60px', padding: '2px 6px', borderRadius: '4px', border: '1px solid var(--border)', fontSize: '12px' }} />
                      )}
                      <button type="button" onClick={() => removeFeature(key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 2px' }}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="text" placeholder="Feature key" value={newFeatureKey} onChange={e => setNewFeatureKey(e.target.value)} style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '12px' }} />
                  <input type="text" placeholder="Value (true/false/number)" value={newFeatureVal} onChange={e => setNewFeatureVal(e.target.value)} style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '12px' }} />
                  <button type="button" onClick={addFeature} style={{ padding: '6px 12px', borderRadius: '6px', background: 'var(--accent)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Plus size={12} /> Add
                  </button>
                </div>
              </div>
            </div>

            {/* Licensed Modules — catalog-driven. Tier-derived modules (active
                module_grants) render checked+disabled with an "included in
                {tier}" hint so the operator never double-sets what the license
                already bundles. */}
            <div className="card">
              <div className="card-header"><span className="card-title">Licensed Modules</span></div>
              <div className="card-body">
                {moduleCatalog.length === 0 ? (
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No modules in the catalog.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {moduleCatalog.map(m => {
                      const derivedHere = derivedModulesForTier(form.tier, tierGrants)[m.code];
                      const isDerived = !!derivedHere;
                      const checked = isDerived || moduleSel[m.code] === true;
                      const term = derivedHere?.termDays ?? null;
                      return (
                        <label
                          key={m.code}
                          style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: isDerived ? 'default' : 'pointer', opacity: isDerived ? 0.85 : 1 }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isDerived}
                            onChange={e => setModuleSel(prev => ({ ...prev, [m.code]: e.target.checked }))}
                            style={{ marginTop: '2px', accentColor: 'var(--accent)', width: '14px', height: '14px', flexShrink: 0, cursor: isDerived ? 'not-allowed' : 'pointer' }}
                          />
                          <div>
                            <span style={{ fontSize: '13px', fontWeight: 500 }}>
                              {m.name}
                              {isDerived && (
                                <span style={{ marginLeft: '8px', fontSize: '10.5px', fontWeight: 600, padding: '1px 7px', borderRadius: 99, background: 'var(--accent-light)', color: 'var(--accent)', textTransform: 'capitalize' }}>
                                  Included in {form.tier}{term != null ? ` · ${term}-day` : ''}
                                </span>
                              )}
                            </span>
                            {m.description && (
                              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px' }}>
                                {m.description}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Metadata */}
            <div className="card">
              <div className="card-header"><span className="card-title">Metadata (JSON)</span></div>
              <div className="card-body">
                <textarea
                  value={form.metadata}
                  onChange={e => set('metadata', e.target.value)}
                  rows={5}
                  placeholder='{"contact_email": "admin@company.com", "contract_id": "CTR-001"}'
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${errors.metadata ? '#DC2626' : 'var(--border)'}`, fontSize: '12px', fontFamily: 'monospace', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
                />
                {errors.metadata && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '4px' }}>{errors.metadata}</div>}
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
                  Product name, grace period, API call cap, hardware ID, and feature flags above are merged into this
                  metadata object on submit — the backend only accepts a small fixed set of top-level fields.
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar summary */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Summary</span></div>
              <div className="card-body">
                <table style={{ width: '100%' }}>
                  <tbody>
                    {summaryRows.map(([k, v]) => (
                      <tr key={String(k)}>
                        <td style={{ padding: '6px 0', fontSize: '12px', color: 'var(--text-muted)', width: '45%' }}>{k}</td>
                        <td style={{ padding: '6px 0', fontSize: '12px', fontWeight: 500 }}>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title">Tier Presets</span></div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {['trial', 'startup', 'professional', 'enterprise'].map(tier => (
                  <button
                    key={tier}
                    type="button"
                    onClick={() => applyTierPreset(tier)}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: `1px solid ${form.tier === tier ? 'var(--accent)' : 'var(--border)'}`, background: form.tier === tier ? 'var(--accent-light)' : 'var(--surface)', color: form.tier === tier ? 'var(--accent)' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px', fontWeight: form.tier === tier ? 600 : 400, textTransform: 'capitalize', textAlign: 'left' }}
                  >
                    {tier}
                    <span style={{ fontSize: '11px', marginLeft: '6px', color: 'var(--text-muted)' }}>
                      {TIER_PRESETS[tier].vendorLimit} seats
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{ padding: '12px', borderRadius: '8px', background: loading ? 'var(--accent-muted)' : 'var(--accent)', color: 'white', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 600 }}
            >
              {loading ? 'Creating...' : 'Issue License'}
            </button>
            <Link to="/licenses" style={{ textAlign: 'center', fontSize: '13px', color: 'var(--text-secondary)', textDecoration: 'none' }}>
              Cancel
            </Link>
          </div>
        </div>
      </form>
      )}
    </div>
  );
}
